/**
 * SimpleWizardPage — `/simple`. Public, no auth, no shell.
 *
 * Three-step front door for users who don't yet know what
 * "cryptographic signing" means. They know their job title and they
 * know their pain. We map both to one of the 6 Official products and
 * a launch CTA (live demo, Spark wizard, or creator store).
 *
 *   Step 1 — "What do you do?"     → 7 domain cards
 *   Step 2 — "What's your biggest pain?" → 3-4 pain cards per domain
 *   Step 3 — "Here's what we recommend"  → product card + Try it now
 *
 * No state machine library — three-stage local state is enough. The
 * `?d=&p=` query string lets a deep-link land mid-wizard for sharing
 * specific recommendations on social.
 *
 * Lives outside `ProtectedRoute` so a cold visitor can run the whole
 * flow without a sign-up wall. The "Try it now" CTAs naturally hit
 * authenticated routes when needed (`/spark` requires login;
 * `/demo` and `/store` don't).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { GiOrbitLogo } from '@/components/GiOrbitLogo';
import {
  OFFICIAL_PRODUCT_IDS,
  getOfficialListings,
  getOfficialOverview,
} from '@/data/defaultProducts';
import type { Locale } from '@/i18n/config';
import { useLocaleStore } from '@/stores/localeStore';

const ACCENT = '#6366f1';

// ─── Data ──────────────────────────────────────────────────────────

type CtaKind = 'demo' | 'spark' | 'store';

interface Pain {
  id: string;
  label_en: string;
  label_he: string;
  /** EntityId of the matching Official product. */
  product_ref: string;
  cta: CtaKind;
  /** Demo persona for `cta === 'demo'`; product slug for `cta === 'spark'`. */
  cta_target: string;
}

interface Domain {
  id: string;
  icon: string;
  name_en: string;
  name_he: string;
  blurb_en: string;
  blurb_he: string;
  pains: Pain[];
}

const DOMAINS: Domain[] = [
  {
    id: 'food',
    icon: '🍞',
    name_en: 'Food manufacturer',
    name_he: 'Food manufacturer',
    blurb_en: 'Production lines, cold chains, supplier audits.',
    blurb_he: 'Production lines, Cold chain, Vendor audits.',
    pains: [
      {
        id: 'inspection',
        label_en: 'A surprise inspection arrives and I can\'t prove the chain.',
        label_he: 'A surprise audit arrives and I cannot prove the chain.',
        product_ref: OFFICIAL_PRODUCT_IDS.gateToShelf,
        cta: 'demo',
        cta_target: 'gate-to-shelf',
      },
      {
        id: 'cold-chain',
        label_en: 'Cold-chain readings are scattered across spreadsheets.',
        label_he: 'Cold-chain readings scattered across spreadsheets.',
        product_ref: OFFICIAL_PRODUCT_IDS.gateToShelf,
        cta: 'demo',
        cta_target: 'gate-to-shelf',
      },
      {
        id: 'recall',
        label_en: 'A failed batch is being recalled and I can\'t trace it.',
        label_he: 'A batch failed and we need to act recall — and it cannot be tracked.',
        product_ref: OFFICIAL_PRODUCT_IDS.gateToShelf,
        cta: 'demo',
        cta_target: 'gate-to-shelf',
      },
      {
        id: 'supplier',
        label_en: 'Supplier audits are reconstructed every quarter.',
        label_he: 'Vendor audits reconstructed from scratch every quarter.',
        product_ref: OFFICIAL_PRODUCT_IDS.gateToShelf,
        cta: 'demo',
        cta_target: 'gate-to-shelf',
      },
    ],
  },
  {
    id: 'medical',
    icon: '🏥',
    name_en: 'Medical device / hospital',
    name_he: 'Medical equipment / hospital',
    blurb_en: 'AMAR clock, incident reports, IEC 62304.',
    blurb_he: 'Clock AMAR, Incident reports, IEC 62304.',
    pains: [
      {
        id: 'amar-clock',
        label_en: 'AMAR\'s 48-hour clock is running and the timeline is in Slack.',
        label_he: 'The medical-device 48-hour clock is running — and the timeline is-Slack.',
        product_ref: OFFICIAL_PRODUCT_IDS.amarReporter,
        cta: 'demo',
        cta_target: 'amar-reporter',
      },
      {
        id: 'malfunction',
        label_en: 'A device malfunctioned and the report has to leave today.',
        label_he: 'A device failed and the report must go out today.',
        product_ref: OFFICIAL_PRODUCT_IDS.amarReporter,
        cta: 'demo',
        cta_target: 'amar-reporter',
      },
      {
        id: 'audit-trail',
        label_en: 'My audit trail is screenshots and forwarded emails.',
        label_he: 'My audit trail is screenshots and forwarded emails.',
        product_ref: OFFICIAL_PRODUCT_IDS.amarReporter,
        cta: 'demo',
        cta_target: 'amar-reporter',
      },
    ],
  },
  {
    id: 'municipality',
    icon: '🏛️',
    name_en: 'Municipality / council',
    name_he: 'Municipality / council',
    blurb_en: 'Citizen requests, FOIA, comptroller, procurement.',
    blurb_he: 'Resident requests, Freedom of information, Comptroller, Tenders.',
    pains: [
      {
        id: 'requests-vanish',
        label_en: 'Citizen requests disappear; the resident calls the mayor.',
        label_he: 'Resident requests disappear; The resident calls the mayor.',
        product_ref: OFFICIAL_PRODUCT_IDS.citizenMirror,
        cta: 'demo',
        cta_target: 'citizen-mirror',
      },
      {
        id: 'foia',
        label_en: 'FOIA requests are timing out before we even read them.',
        label_he: 'Freedom-of-information requests expire before we even read them.',
        product_ref: OFFICIAL_PRODUCT_IDS.citizenMirror,
        cta: 'demo',
        cta_target: 'citizen-mirror',
      },
      {
        id: 'comptroller',
        label_en: 'The State Comptroller is asking for a file we have to reconstruct overnight.',
        label_he: 'The State Comptroller requests a file we must reconstruct overnight.',
        product_ref: OFFICIAL_PRODUCT_IDS.citizenMirror,
        cta: 'demo',
        cta_target: 'citizen-mirror',
      },
      {
        id: 'procurement',
        label_en: 'Procurement decisions live across three different systems.',
        label_he: 'Procurement decisions live in three different systems.',
        product_ref: OFFICIAL_PRODUCT_IDS.citizenMirror,
        cta: 'demo',
        cta_target: 'citizen-mirror',
      },
    ],
  },
  {
    id: 'university',
    icon: '🎓',
    name_en: 'University / research lab',
    name_he: 'University / research lab',
    blurb_en: 'Authorship, data leaks, grant audits, AI muddle.',
    blurb_he: 'Copyright, Data leaks, Grant audit, AI.',
    pains: [
      {
        id: 'authorship',
        label_en: 'Someone is about to claim my dataset as theirs.',
        label_he: 'Someone is about to claim my dataset is theirs.',
        product_ref: OFFICIAL_PRODUCT_IDS.researchShield,
        cta: 'demo',
        cta_target: 'research-shield',
      },
      {
        id: 'plagiarism',
        label_en: 'A student claims plagiarism and I can\'t prove the order.',
        label_he: 'A student claims plagiarism and I cannot prove the order.',
        product_ref: OFFICIAL_PRODUCT_IDS.researchShield,
        cta: 'demo',
        cta_target: 'research-shield',
      },
      {
        id: 'grant-audit',
        label_en: 'A grant agency is auditing the lab\'s figure history.',
        label_he: 'A funding body audits the lab\'s records history.',
        product_ref: OFFICIAL_PRODUCT_IDS.researchShield,
        cta: 'demo',
        cta_target: 'research-shield',
      },
    ],
  },
  {
    id: 'law',
    icon: '⚖️',
    name_en: 'Law firm / litigation',
    name_he: 'Law firm / litigation',
    blurb_en: 'Chain of custody, FRE 902, Daubert.',
    blurb_he: 'Chain of custody, FRE 902, Daubert.',
    pains: [
      {
        id: 'custody-broken',
        label_en: 'The chain of custody on Exhibit C is questionable.',
        label_he: 'Chain of custody for exhibit C\' In doubt.',
        product_ref: OFFICIAL_PRODUCT_IDS.legalEvidence,
        cta: 'spark',
        cta_target: 'legal-evidence',
      },
      {
        id: 'opposing-counsel',
        label_en: 'Opposing counsel will ask "who held this between two timestamps".',
        label_he: 'The opposing side will ask "Who held this between two timestamps".',
        product_ref: OFFICIAL_PRODUCT_IDS.legalEvidence,
        cta: 'spark',
        cta_target: 'legal-evidence',
      },
      {
        id: 'document-priority',
        label_en: 'I need to prove a document existed on a specific date.',
        label_he: 'Need to prove a document existed on a certain date.',
        product_ref: OFFICIAL_PRODUCT_IDS.legalEvidence,
        cta: 'spark',
        cta_target: 'legal-evidence',
      },
    ],
  },
  {
    id: 'security',
    icon: '🛡️',
    name_en: 'Security team / SOC',
    name_he: 'Security team / SOC',
    blurb_en: 'NIST 800-61, NIS2, IOCs, forensic chain.',
    blurb_he: 'NIST 800-61, NIS2, IOCs, Forensic chain.',
    pains: [
      {
        id: 'breach-timeline',
        label_en: 'A breach happened and the timeline is screenshots and tickets.',
        label_he: 'There was a breach — and the timeline is screenshots and-tickets.',
        product_ref: OFFICIAL_PRODUCT_IDS.cyberShield,
        cta: 'demo',
        cta_target: 'cyber-shield',
      },
      {
        id: 'audit',
        label_en: 'A NIST 800-61 audit asks for the chain on every IOC.',
        label_he: 'Audit NIST 800-61 Requires a chain for every IOC.',
        product_ref: OFFICIAL_PRODUCT_IDS.cyberShield,
        cta: 'demo',
        cta_target: 'cyber-shield',
      },
      {
        id: 'litigation',
        label_en: 'My forensic evidence has to survive a Daubert hearing.',
        label_he: 'My forensic evidence must survive a hearing Daubert.',
        product_ref: OFFICIAL_PRODUCT_IDS.cyberShield,
        cta: 'demo',
        cta_target: 'cyber-shield',
      },
    ],
  },
  {
    id: 'creator',
    icon: '✨',
    name_en: 'Creator / freelancer',
    name_he: 'Creator / freelancer',
    blurb_en: 'Code, ideas, freelance contracts, original work.',
    blurb_he: 'Code, Ideas, Freelancer agreements, Original work.',
    pains: [
      {
        id: 'code-theft',
        label_en: 'A client may claim my code is theirs.',
        label_he: 'A client may claim my code is theirs.',
        product_ref: OFFICIAL_PRODUCT_IDS.codeProof,
        cta: 'spark',
        cta_target: 'code-proof',
      },
      {
        id: 'idea-stolen',
        label_en: 'Someone is about to publish my idea without crediting me.',
        label_he: 'Someone is about to publish my idea without crediting me.',
        product_ref: OFFICIAL_PRODUCT_IDS.ideaProof,
        cta: 'spark',
        cta_target: 'idea-proof',
      },
      {
        id: 'freelance-dispute',
        label_en: 'A freelance client is disputing what I delivered.',
        label_he: 'A freelance client disputes what I delivered.',
        product_ref: OFFICIAL_PRODUCT_IDS.freelanceContract,
        cta: 'spark',
        cta_target: 'freelance-contract',
      },
    ],
  },
];

// ─── Component ─────────────────────────────────────────────────────

type Stage = 'domain' | 'pain' | 'recommendation';

export function SimpleWizardPage() {
  const locale = useLocaleStore((s) => s.locale);
  const [search, setSearch] = useSearchParams();

  // Initial state seeded from `?d=&p=` so a recommendation is shareable.
  const initialDomain = DOMAINS.find((d) => d.id === search.get('d')) ?? null;
  const initialPain =
    initialDomain?.pains.find((p) => p.id === search.get('p')) ?? null;
  const initialStage: Stage = initialPain ? 'recommendation' : initialDomain ? 'pain' : 'domain';

  const [domain, setDomain] = useState<Domain | null>(initialDomain);
  const [pain, setPain] = useState<Pain | null>(initialPain);
  const [stage, setStage] = useState<Stage>(initialStage);

  // Keep the URL in sync so back/forward browser navigation matches
  // wizard state and a copied URL drops the next visitor in the same
  // place.
  useEffect(() => {
    const next = new URLSearchParams();
    if (domain) next.set('d', domain.id);
    if (pain) next.set('p', pain.id);
    setSearch(next, { replace: true });
  }, [domain, pain, setSearch]);

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <main className="mx-auto flex min-h-screen max-w-2xl flex-col px-5 pt-8 pb-10">
        {/* Top bar */}
        <div className="flex items-center justify-between text-sm">
          <Link
            to="/"
            className="flex items-center gap-2 text-ink-muted hover:text-ink"
          >
            <GiOrbitLogo size="sm" />
            <span className="font-semibold">Growing Intelligence</span>
          </Link>
          <Link to="/marketplace" className="text-ink-muted hover:text-ink">
            {locale === 'he' ? 'Skip' : 'Skip'}
          </Link>
        </div>

        {/* Progress dots */}
        <div className="mt-7 flex items-center justify-center gap-2">
          {(['domain', 'pain', 'recommendation'] as const).map((s, idx) => {
            const reached = stageRank(stage) >= idx;
            return (
              <span
                key={s}
                className={`h-1.5 w-8 rounded-full transition-colors ${
                  reached ? '' : 'bg-zinc-200'
                }`}
                style={reached ? { backgroundColor: ACCENT } : undefined}
                aria-hidden
              />
            );
          })}
        </div>

        <div className="mt-8 flex-1">
          {stage === 'domain' ? (
            <DomainStep
              locale={locale}
              onPick={(d) => {
                setDomain(d);
                setPain(null);
                setStage('pain');
              }}
            />
          ) : stage === 'pain' && domain ? (
            <PainStep
              domain={domain}
              locale={locale}
              onBack={() => {
                setDomain(null);
                setPain(null);
                setStage('domain');
              }}
              onPick={(p) => {
                setPain(p);
                setStage('recommendation');
              }}
            />
          ) : stage === 'recommendation' && domain && pain ? (
            <RecommendationStep
              domain={domain}
              pain={pain}
              locale={locale}
              onBack={() => setStage('pain')}
              onRestart={() => {
                setDomain(null);
                setPain(null);
                setStage('domain');
              }}
            />
          ) : (
            // Fallback: bad URL — bounce to step 1.
            <DomainStep
              locale={locale}
              onPick={(d) => {
                setDomain(d);
                setPain(null);
                setStage('pain');
              }}
            />
          )}
        </div>

        <footer className="mt-10 text-center text-xs text-ink-subtle">
          <p>
            {locale === 'he' ? (
              <>
                Already a user?{' '}
                <Link
                  to="/login"
                  className="font-medium hover:underline"
                  style={{ color: ACCENT }}
                >
                  Log in here
                </Link>
              </>
            ) : (
              <>
                Already a user?{' '}
                <Link
                  to="/login"
                  className="font-medium hover:underline"
                  style={{ color: ACCENT }}
                >
                  Log in
                </Link>
              </>
            )}
          </p>
        </footer>
      </main>
    </div>
  );
}

// ─── Steps ─────────────────────────────────────────────────────────

function DomainStep({
  locale,
  onPick,
}: {
  locale: Locale;
  onPick: (d: Domain) => void;
}) {
  const heading = locale === 'he' ? 'What do you do?' : 'What do you do?';
  const sub =
    locale === 'he'
      ? 'Pick the closest. We will point you to the right tool.'
      : 'Pick the closest. We\'ll point you at the right tool.';
  return (
    <section>
      <h1 className="text-3xl font-semibold tracking-tight">{heading}</h1>
      <p className="mt-2 text-sm text-ink-muted">{sub}</p>
      <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {DOMAINS.map((d) => (
          <li key={d.id}>
            <button
              type="button"
              onClick={() => onPick(d)}
              className="flex w-full items-start gap-3 rounded-2xl border border-border bg-surface p-4 text-start transition-shadow hover:border-spark hover:shadow-raised"
            >
              <span className="text-3xl" aria-hidden>
                {d.icon}
              </span>
              <span className="flex flex-col gap-0.5">
                <span className="text-base font-semibold text-ink">
                  {locale === 'he' ? d.name_he : d.name_en}
                </span>
                <span className="text-xs text-ink-muted">
                  {locale === 'he' ? d.blurb_he : d.blurb_en}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function PainStep({
  domain,
  locale,
  onBack,
  onPick,
}: {
  domain: Domain;
  locale: Locale;
  onBack: () => void;
  onPick: (p: Pain) => void;
}) {
  const heading =
    locale === 'he' ? 'What is your biggest pain?' : 'What\'s your biggest pain?';
  const sub =
    locale === 'he'
      ? 'Answer how it feels. We will pick a product for you.'
      : 'Answer like it feels. We\'ll pick a product for you.';
  return (
    <section>
      <button
        type="button"
        onClick={onBack}
        className="mb-3 inline-flex items-center gap-1 text-xs text-ink-muted hover:text-ink"
      >
        <span aria-hidden>{locale === 'he' ? '→' : '←'}</span>
        {locale === 'he' ? 'Back' : 'Back'}
      </button>
      <div className="flex items-center gap-3">
        <span className="text-3xl" aria-hidden>{domain.icon}</span>
        <h1 className="text-3xl font-semibold tracking-tight">{heading}</h1>
      </div>
      <p className="mt-2 text-sm text-ink-muted">{sub}</p>
      <ul className="mt-6 flex flex-col gap-3">
        {domain.pains.map((p) => (
          <li key={p.id}>
            <button
              type="button"
              onClick={() => onPick(p)}
              className="w-full rounded-2xl border border-border bg-surface p-4 text-start transition-shadow hover:border-spark hover:shadow-raised"
            >
              <span className="text-base text-ink">
                {locale === 'he' ? p.label_he : p.label_en}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function RecommendationStep({
  domain,
  pain,
  locale,
  onBack,
  onRestart,
}: {
  domain: Domain;
  pain: Pain;
  locale: Locale;
  onBack: () => void;
  onRestart: () => void;
}) {
  const heading =
    locale === 'he' ? 'Here is what we will recommend' : 'Here\'s what we recommend';

  // Resolve product copy for the chosen pain.
  const product = useMemo(() => {
    const officials = getOfficialListings(locale === 'he' ? 'he' : 'en');
    return officials.find((p) => p.root_id === pain.product_ref) ?? null;
  }, [pain.product_ref, locale]);

  const overview = getOfficialOverview(
    pain.product_ref,
    locale === 'he' ? 'he' : 'en',
  );

  // CTA wiring per cta kind.
  const ctaHref =
    pain.cta === 'demo'
      ? `/playground?persona=${pain.cta_target}`
      : pain.cta === 'spark'
        ? `/spark?product=${pain.cta_target}`
        : `/store/${pain.cta_target}`;
  const ctaLabel =
    pain.cta === 'demo'
      ? locale === 'he' ? 'Try now (live demo)' : 'Try it now (live demo)'
      : pain.cta === 'spark'
        ? locale === 'he' ? 'Start building' : 'Start building'
        : locale === 'he' ? 'View the catalog' : 'See the catalogue';

  return (
    <section>
      <BackBar onBack={onBack} locale={locale} />
      <h1 className="text-3xl font-semibold tracking-tight">{heading}</h1>

      {/* Pain echo so the user sees we heard them. */}
      <p className="mt-2 text-sm text-ink-muted">
        {locale === 'he' ? 'Because' : 'Because '}
        <span className="font-medium text-ink">
          {locale === 'he' ? pain.label_he : pain.label_en}
        </span>
      </p>

      {product ? (
        <article
          className="mt-5 flex flex-col gap-4 rounded-2xl border bg-surface p-5"
          style={{ borderColor: '#c7d2fe' }}
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <span
                className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide"
                style={{ backgroundColor: '#eef2ff', color: ACCENT }}
              >
                {locale === 'he' ? 'Official product' : 'Official product'}
              </span>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight">
                {product.name}
              </h2>
              <p className="mt-1 text-sm text-ink-muted">{product.tagline}</p>
            </div>
            <div className="text-end">
              <a
                href="mailto:shalev.yoni@gmail.com"
                className="text-sm font-semibold hover:underline"
                style={{ color: ACCENT }}
              >
                Contact Us
              </a>
            </div>
          </div>

          {overview ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-border bg-canvas p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-danger">
                  {locale === 'he' ? 'The pain' : 'The pain'}
                </div>
                <p className="mt-1.5 text-sm leading-relaxed text-ink">
                  {overview.pain}
                </p>
              </div>
              <div className="rounded-xl border border-border bg-canvas p-3">
                <div
                  className="text-[11px] font-semibold uppercase tracking-wide"
                  style={{ color: ACCENT }}
                >
                  {locale === 'he' ? 'The solution' : 'The solution'}
                </div>
                <p className="mt-1.5 text-sm leading-relaxed text-ink">
                  {overview.solution}
                </p>
              </div>
            </div>
          ) : null}

          <div className="mt-1 flex flex-wrap gap-2">
            <a
              href={ctaHref}
              className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90"
              style={{ backgroundColor: ACCENT }}
            >
              {ctaLabel}
            </a>
            <Link
              to={`/marketplace/${product.root_id}`}
              className="inline-flex items-center justify-center rounded-md border border-border bg-surface px-4 py-2 text-sm font-medium text-ink hover:border-spark"
            >
              {locale === 'he' ? 'Full details' : 'See full details'}
            </Link>
            <button
              type="button"
              onClick={onRestart}
              className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm text-ink-muted hover:text-ink"
            >
              {locale === 'he' ? 'Start over' : 'Start over'}
            </button>
          </div>
        </article>
      ) : null}

      <p className="mt-6 text-center text-xs text-ink-subtle">
        {locale === 'he'
          ? `${domain.icon} ${domain.name_he} · We are not trying to sell — just to match`
          : `${domain.icon} ${domain.name_en} · We're not selling, just matching`}
      </p>
    </section>
  );
}

function BackBar({ onBack, locale }: { onBack: () => void; locale: Locale }) {
  return (
    <button
      type="button"
      onClick={onBack}
      className="mb-3 inline-flex items-center gap-1 text-xs text-ink-muted hover:text-ink"
    >
      <span aria-hidden>{locale === 'he' ? '→' : '←'}</span>
      {locale === 'he' ? 'Back' : 'Back'}
    </button>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────

function stageRank(s: Stage): number {
  return s === 'domain' ? 0 : s === 'pain' ? 1 : 2;
}
